
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { User } from '@/modules/authentication/models/user';
import * as UsersService from '@/modules/authentication/services/usersService';
import * as LocationService from '@/modules/nomenclature/services/locationService';
import * as ProjectService from '@/modules/nomenclature/services/projectService';
import { AnalysisRequest, AnalysisRequestSubject, emptyRequest } from '@/modules/analysis/models/analysisRequest';
import { Location } from '@/modules/nomenclature/models/location';
import { BudgetLine } from '@/modules/nomenclature/models/budgetLine';
import SubjectLocationPicker from './inputs/SubjectLocationPicker.vue';
import SubjectBudgetLinePicker from './inputs/SubjectBudgetLinePicker.vue';

@Component({
  components: { SubjectLocationPicker, SubjectBudgetLinePicker }
})
export default class AnalysisSubject extends Vue {
  @Prop({ required: false, default: null })
  protected value?: AnalysisRequest | null;
  @Prop({ required: false, default: false })
  protected displayError?: boolean;
  protected locationLocked = false;
  protected location: Location | null = null;
  protected budgetLineLocked = false;
  protected budgetLine: BudgetLine | null = null;
  protected datepickerOpen = false;
  private subject: AnalysisRequestSubject = emptyRequest().subject;
  private user?: User;

  // REASON
  protected reasons = [
    {
      text: this.$t('analysis.AnalysisSubject.reasonEnum.event').toString(),
      value: 'event'
    },
    {
      text: this.$t('analysis.AnalysisSubject.reasonEnum.waste').toString(),
      value: 'waste'
    },
    {
      text: this.$t('analysis.AnalysisSubject.reasonEnum.construction').toString(),
      value: 'construction'
    },
    {
      text: this.$t('analysis.AnalysisSubject.reasonEnum.dismantling').toString(),
      value: 'dismantling'
    },
    {
      text: this.$t('analysis.AnalysisSubject.reasonEnum.cleanup').toString(),
      value: 'cleanup'
    },
    {
      text: this.$t('analysis.AnalysisSubject.reasonEnum.assessment').toString(),
      value: 'assessment'
    },
    {
      text: this.$t('analysis.AnalysisSubject.reasonEnum.other').toString(),
      value: 'other'
    }
  ];

  protected mounted(): void {
    this.applyUserRestrictedFields();
  }

  @Watch('value', { deep: true, immediate: true })
  protected onValueChange(): void {
    if (this.value) {
      Object.assign(this.subject, this.value.subject);
    }
    const newLocationId = this.value?.subject.locationId;
    const newBudgetLineId = this.value?.subject.budgetLineId;

    if (this.location?.id !== newLocationId) {
      if (newLocationId) {
        this.onLocationChange(newLocationId);
      } else {
        this.location = null;
      }
    }
    if (this.budgetLine?.id !== newBudgetLineId) {
      if (newBudgetLineId) {
        this.onBudgetLineChange(newBudgetLineId);
      } else {
        this.budgetLine = null;
      }
    }
    this.applyUserRestrictedFields();
  }

  /**
   * The user has selected a location: use it as an argument to retrieve the current budgets here.
   */
  protected onLocationChange(locationId: string): void {
    LocationService.fetchById(locationId).then((response) => {
      this.location = response.data;
    });
    this.subject.locationId = locationId;
    this.$emit('change', this.subject);
  }

  /**
   * The user wants to chose another location : we have to clear other choices
   */
  protected onLocationClear(): void {
    this.location = null;
  }

  /**
   * The user has selected a budget line inside project.
   */
  protected onBudgetLineChange(budgetLineId: string): void {
    ProjectService.fetchBudgetLineById(budgetLineId).then((response) => {
      const budgetLineRcv = response.data;
      if (budgetLineRcv.id) {
        this.budgetLine = budgetLineRcv;
        this.subject.budgetLineId = budgetLineRcv.id;
      }
    });
    this.$emit('change', this.subject);
  }

  /**
   * The user wants to chose another location : we have to clear other choices
   */
  protected onBudgetLineClear(): void {
    this.budgetLine = null;
  }

  protected applyUserRestrictedFields(): void {
    if (!this.user) {
      UsersService.me().then((response) => {
        this.user = response.data;
        if (this.user.locationId) {
          this.onLocationChange(this.user.locationId);
          this.locationLocked = true;
        } else {
          this.locationLocked = false;
        }
        if (this.user.budgetLineId) {
          this.onBudgetLineChange(this.user.budgetLineId);
          this.budgetLineLocked = true;
        } else {
          this.budgetLineLocked = false;
        }
      });
    }
  }
}
